import { ApiError } from '../core/api/http'
import { byStart } from '../core/format/dates'
import { isOwnerId } from '../core/rules/salonDays'
import { isRoleId, lastOwnerProblem, selectableRoles } from '../core/rules/permissions'
import { customerName } from '../admin/customers/customerRules'
import { staff } from '../data/staff'
import { db } from './db'
import { reply } from './reply'
import { demoAdminIdentity } from './adminIdentity'
import { demoAudit, demoTeam, type DemoAccount } from './teamStore'
import { demoTwoFactor } from './twoFactor'
import type { AdminBackend } from '../core/types/backend'
import type { AuditEntry } from '../core/types/team'

/**
 * DEMO — Team en toegang (docs/RECHTEN-EN-SCHAAL.md §10.5), het logboek (§10.7) en de beperkte
 * klantkaart. De server bewaart accounts los van de personen, stuurt de uitnodiging en dwingt 2FA af.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const AUDIT_PAGE = 50

const owners = () => demoTeam.list().map((a) => ({ accountId: a.accountId, roles: a.roles, active: demoTeam.usable(a) }))

function accountOf(staffId: string): DemoAccount {
  const a = demoTeam.byStaff(staffId)
  if (!a) throw new ApiError('Deze persoon heeft geen login.', 404)
  return a
}

function guardLastOwner(a: DemoAccount, change: { roles?: DemoAccount['roles']; stop?: boolean }) {
  const problem = lastOwnerProblem(owners(), a.accountId, change)
  if (problem) throw new ApiError(problem, 409, undefined, 'last_owner')
}

type Calls =
  | 'fetchStaffLogins'
  | 'inviteStaff'
  | 'revokeStaffLogin'
  | 'updateStaffAccess'
  | 'stopStaff'
  | 'revokeStaffSessions'
  | 'reauth'
  | 'fetchAuditLog'
  | 'fetchStaffCustomer'

export const demoStaffBackend: Pick<AdminBackend, Calls> = {
  fetchStaffLogins: () => reply(demoTeam.list().map((a) => demoTeam.toLogin(a, demoAdminIdentity.current().staffId)), 200),

  inviteStaff(staffId, { email }) {
    const profile = staff.find((s) => s.id === staffId)
    if (!profile || isOwnerId(staffId)) throw new ApiError('Deze medewerker bestaat niet (meer).', 404)
    const clean = email.trim().toLowerCase()
    if (!EMAIL.test(clean)) throw new ApiError('Vul een geldig e-mailadres in.', 422, { email: 'Vul een geldig e-mailadres in.' })
    const taken = demoTeam.list().some((l) => l.email === clean && l.staffId !== staffId)
    if (taken) throw new ApiError('Dit e-mailadres hoort al bij een andere login.', 409, { email: 'Dit e-mailadres is al in gebruik.' }, 'email_taken')
    const known = demoTeam.byStaff(staffId)
    const account: DemoAccount = known
      ? { ...known, email: clean, status: 'uitgenodigd' }
      : { accountId: `acc-${staffId}`, staffId, email: clean, status: 'uitgenodigd', roles: ['behandelaar-zzp'], validUntil: null, epoch: 0, sessions: [] }
    demoTeam.put(account)
    return reply(demoTeam.toLogin(account, demoAdminIdentity.current().staffId), 400)
  },

  // Idempotent: al ingetrokken = ook goed. Sessies vervallen mee.
  async revokeStaffLogin(staffId) {
    const a = demoTeam.byStaff(staffId)
    if (a) {
      guardLastOwner(a, { stop: true })
      demoTeam.remove(staffId)
    }
    await reply(null, 300)
  },

  updateStaffAccess(staffId, { roles, validUntil }) {
    const a = accountOf(staffId)
    const allowed = new Set(selectableRoles().map((r) => r.id))
    const clean = [...new Set(roles)].filter(isRoleId)
    if (clean.length === 0 || clean.some((r) => !allowed.has(r))) throw new ApiError('Kies een rol.', 422, { roles: 'Kies een rol.' })
    if (validUntil !== null && !/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) throw new ApiError('Kies een geldige datum.', 422, { validUntil: 'Kies een geldige datum.' })
    guardLastOwner(a, { roles: clean })
    if (validUntil !== null && clean.includes('eigenaar')) guardLastOwner(a, { stop: true })
    const next = demoTeam.change(staffId, (x) => ({ ...x, roles: clean, validUntil }))
    return reply(demoTeam.toLogin(next ?? a, demoAdminIdentity.current().staffId), 300)
  },

  // Stoppen: login en alle sessies in één keer weg. Idempotent. (De server zet in dezelfde
  // transactie ook de persoon op inactief; in de demo doet het beheer dat via het concept.)
  async stopStaff(staffId) {
    const a = demoTeam.byStaff(staffId)
    if (a) {
      guardLastOwner(a, { stop: true })
      demoTeam.remove(staffId)
    }
    await reply(null, 350)
  },

  revokeStaffSessions(staffId) {
    accountOf(staffId)
    const next = demoTeam.revokeSessions(staffId)
    if (!next) throw new ApiError('Deze persoon heeft geen login.', 404)
    return reply(demoTeam.toLogin(next, demoAdminIdentity.current().staffId), 300)
  },

  async reauth({ password, code }) {
    const expected = demoAdminIdentity.person().password
    const codeOk = !demoTwoFactor.enabled() || (code ? await demoTwoFactor.check({ code }) : false)
    if (password !== expected || !codeOk) {
      await reply(null, 400)
      throw new ApiError(
        demoTwoFactor.enabled() ? 'Je wachtwoord of code klopt niet.' : 'Dat wachtwoord klopt niet.',
        422,
        password !== expected ? { password: 'Dat wachtwoord klopt niet.' } : { code: 'Die code klopt niet.' },
      )
    }
    demoTeam.reauthed()
    await reply(null, 300)
  },

  fetchAuditLog(query) {
    const match = (e: AuditEntry) =>
      (!query.accountId || e.accountId === query.accountId) &&
      (!query.cls || e.cls === query.cls) &&
      (!query.targetId || e.target.id === query.targetId) &&
      (!query.from || e.at.slice(0, 10) >= query.from) &&
      (!query.to || e.at.slice(0, 10) <= query.to)
    const all = demoAudit.list().filter(match)
    const pageCount = Math.max(1, Math.ceil(all.length / AUDIT_PAGE))
    const page = Math.min(Math.max(1, query.page ?? 1), pageCount)
    return reply({ entries: all.slice((page - 1) * AUDIT_PAGE, page * AUDIT_PAGE), page, pageCount, total: all.length }, 250)
  },

  fetchStaffCustomer(id) {
    const account = db.account(id)
    if (!account) throw new ApiError('Deze klant bestaat niet (meer).', 404)
    const rows = db.appointments().filter((a) => a.customerId === id)
    // Zoals de server: met eigen bereik alleen als de klant een afspraak bij háár heeft.
    const mine = demoAdminIdentity.access().grants['customers.view'] === 'all' ? rows.length > 0 : rows.some((a) => demoAdminIdentity.can('customers.view', a))
    if (!mine) throw new ApiError('Deze klant heeft geen afspraak bij jou.', 403, undefined, 'not_your_customer')
    const treatments = rows
      .filter((r) => r.outcome === 'afgerond')
      .sort((a, b) => byStart(b, a))
      .map((r) => ({
        date: r.date,
        treatmentSlug: r.treatmentSlug,
        staffId: r.staffId ?? 'sarah',
        ...(r.treatmentNote ? { note: r.treatmentNote } : {}),
      }))
    return reply(
      {
        id,
        name: customerName(account.customer),
        ...(account.customer.phone ? { phone: account.customer.phone } : {}),
        plan: account.plan ?? null,
        // Aandachtspunten uit de intake. De demo heeft nog geen intake: alleen de focus van het huidplan.
        attention: account.plan?.focus ? [account.plan.focus] : [],
        treatments,
      },
      250,
    )
  },
}
